/**
 * Tenants list page (refreshed).
 *
 * - SUPER operators see every tenant + "New tenant" action.
 * - TENANT operators see only tenants they were granted on
 *   (server-side visibility filter).
 */
import { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, Plus, RotateCw } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { DataTable, type DataTableColumn } from "@/components/shell/DataTable";
import { EmptyState } from "@/components/shell/EmptyState";
import { FilterChip } from "@/components/shell/FilterChip";
import { PageHeader } from "@/components/shell/PageHeader";
import { Toolbar, FilterGroup } from "@/components/shell/Toolbar";
import {
  getTenants,
  type TenantEnvironment,
  type TenantRow,
  type TenantStatus,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";

const ENVIRONMENTS: readonly TenantEnvironment[] = ["live", "test"] as const;
const STATUSES: readonly TenantStatus[] = ["active", "suspended"] as const;

function tenantInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?";
}

export function TenantsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  const env = (params.get("environment") as TenantEnvironment | null) ?? undefined;
  const status = (params.get("status") as TenantStatus | null) ?? undefined;

  const query = useQuery({
    queryKey: ["tenants", { env, status }] as const,
    queryFn: () => getTenants({ environment: env, status }),
  });

  useEffect(() => {
    if (query.error) {
      toast.error("Couldn't load tenants", {
        description: "Check your network and try again.",
      });
    }
  }, [query.error]);

  function setFilter(key: "environment" | "status", value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const columns = useMemo<ReadonlyArray<DataTableColumn<TenantRow>>>(
    () => [
      {
        key: "name",
        header: "Tenant",
        cell: (r) => (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary/10 text-[10px] text-primary">
                {tenantInitials(r.name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="truncate font-medium">{r.name}</div>
              <div className="truncate font-mono text-xs text-muted-foreground">
                {r.id}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "environment",
        header: "Env",
        cell: (r) => (
          <Badge
            variant={r.environment === "live" ? "success" : "warning"}
            className="capitalize"
          >
            {r.environment}
          </Badge>
        ),
      },
      {
        key: "status",
        header: "Status",
        cell: (r) => (
          <Badge
            variant={r.status === "active" ? "success" : "muted"}
            className="capitalize"
          >
            {r.status}
          </Badge>
        ),
      },
      {
        key: "counts",
        header: "Operators · Keys · Providers",
        className: "text-muted-foreground",
        cell: (r) =>
          r.counts ? (
            <span className="font-mono text-xs tabular-nums">
              {r.counts.operators} · {r.counts.api_keys} · {r.counts.provider_accounts}
            </span>
          ) : (
            "—"
          ),
      },
      {
        key: "created_at",
        header: "Created",
        className: "text-muted-foreground",
        cell: (r) => (
          <span className="whitespace-nowrap text-xs">
            {new Date(r.created_at).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [],
  );

  const isSuper = user?.role === "super";
  const hasNoGrants = !isSuper && (query.data?.total ?? 0) === 0;

  return (
    <>
      <PageHeader
        eyebrow="Organization"
        title="Tenants"
        description="Every Senti customer organization you have access to."
        action={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
            >
              <RotateCw className={query.isFetching ? "animate-spin" : undefined} />
              Refresh
            </Button>
            {isSuper ? (
              <Button asChild size="sm">
                <Link to="/tenants/new">
                  <Plus /> New tenant
                </Link>
              </Button>
            ) : null}
          </>
        }
      />

      <Toolbar
        filters={
          <>
            <FilterGroup label="Env">
              <FilterChip
                active={env === undefined}
                onClick={() => setFilter("environment", null)}
              >
                All
              </FilterChip>
              {ENVIRONMENTS.map((e) => (
                <FilterChip
                  key={e}
                  active={env === e}
                  onClick={() => setFilter("environment", e)}
                >
                  {e}
                </FilterChip>
              ))}
            </FilterGroup>
            <FilterGroup label="Status">
              <FilterChip
                active={status === undefined}
                onClick={() => setFilter("status", null)}
              >
                All
              </FilterChip>
              {STATUSES.map((s) => (
                <FilterChip
                  key={s}
                  active={status === s}
                  onClick={() => setFilter("status", s)}
                >
                  {s}
                </FilterChip>
              ))}
            </FilterGroup>
          </>
        }
      />

      <DataTable<TenantRow>
        columns={columns}
        rows={query.data?.results ?? []}
        loading={query.isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/tenants/${r.id}`)}
        emptyState={
          hasNoGrants ? (
            <EmptyState
              icon={Building2}
              title="No tenants granted yet"
              description="Ask a super-admin to add you to a tenant."
            />
          ) : (
            <EmptyState
              icon={Building2}
              title="No tenants match these filters"
              description="Clear a filter to see more rows, or create a new tenant."
              action={
                isSuper ? (
                  <Button asChild size="sm">
                    <Link to="/tenants/new">
                      <Plus /> New tenant
                    </Link>
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />
    </>
  );
}

export default TenantsListPage;
